import Box from '@mui/material/Box';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import BugReportIcon from '@mui/icons-material/BugReport';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import ComputerIcon from '@mui/icons-material/Computer';
import GetAppIcon from '@mui/icons-material/GetApp';
import SpeedIcon from '@mui/icons-material/Speed';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import WarningIcon from '@mui/icons-material/Warning';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate } from 'react-router';
import { Button, Table } from 'src/common';
import { getPageTitle } from 'src/config/projectConfig';
import { getSecurityNavItemPath, getSecurityVulnerabilityDetailHref, SECURITY_ENDPOINTS_HREF } from 'src/mindtime/config/securityNav';
import {
  SECURITY_PAGE_FILTER_KEYS,
  SECURITY_VULNERABILITY_FILTER_DEFAULTS,
} from 'src/mindtime/constants/securityFilters';
import { useSecurityEdrToolbarReset, useSecurityPageFilters } from 'src/mindtime/hooks/useSecurityPageFilters';
import {
  useWazuhScope,
  WAZUH_DATE_RANGE_OPTIONS,
  type WazuhDateRangeOption,
} from 'src/mindtime/hooks/useWazuhScope';
import {
  EDR_COLORS,
  EdrDataCard,
  EdrDataToolbar,
  EdrDateTime,
  EdrKpiCard,
  EdrPageHeader,
  EdrSeverityPill,
  EdrStatusPill,
  edrMonoStyle,
  edrMutedStyle,
  type EdrToolbarFilter,
} from '../shared/edrPrimitives';
import type { Column, TableData } from 'src/types/table';
import type { WazuhVulnerabilityItem } from 'src/types/mindtime/wazuhVulnerabilities';
import { useVulnerabilitiesData, useVulnerabilityExport, type VulnerabilitySortParams } from './useVulnerabilitiesData';
import {
  getVulnerabilityId,
  toFilterListLabel,
  toVulnerabilityTitle,
  vulnerabilitySeverityToLevel,
  vulnerabilityStatusToTone,
} from './vulnerabilityDetailModel';

const formatNumber = (value?: number | null): string => {
  if (typeof value !== 'number' || Number.isNaN(value)) return '—';
  return value.toLocaleString();
};

const getCvssColor = (score: number) => {
  if (score >= 9) return EDR_COLORS.critical;
  if (score >= 7) return EDR_COLORS.high;
  if (score >= 4) return EDR_COLORS.medium;
  return EDR_COLORS.success;
};

const CvssCell: FC<{ score: number }> = ({ score }) => {
  const color = getCvssColor(score);
  const pct = Math.min(100, (score / 10) * 100);
  return (
    <Box display="flex" alignItems="center" style={{ gap: 8 }}>
      <span style={{ ...edrMonoStyle, fontWeight: 700, color, minWidth: 28 }}>
        {typeof score === 'number' ? score.toFixed(1) : '—'}
      </span>
      <Box
        style={{
          flex: 1,
          height: 4,
          borderRadius: 2,
          background: EDR_COLORS.slate200,
          minWidth: 48,
          maxWidth: 72,
        }}
      >
        <Box style={{ width: `${pct}%`, height: '100%', borderRadius: 2, background: color }} />
      </Box>
    </Box>
  );
};

const breadcrumb = {
  links: [
    { name: 'EDR', to: SECURITY_ENDPOINTS_HREF },
    { name: 'Vulnerability Detection', to: getSecurityNavItemPath('vulnerability-detection') ?? '' },
  ],
};

const SEVERITY_OPTIONS = [
  { label: 'Critical', value: 'critical' },
  { label: 'High', value: 'high' },
  { label: 'Medium', value: 'medium' },
  { label: 'Low', value: 'low' },
];

const STATUS_OPTIONS = [
  { label: 'Open', value: 'open' },
  { label: 'In Progress', value: 'in_progress' },
  { label: 'Resolved', value: 'resolved' },
];

const CVSS_OPTIONS = [
  { label: 'Critical (9.0+)', value: '9' },
  { label: 'High (7.0+)', value: '7' },
  { label: 'Medium (4.0+)', value: '4' },
  { label: 'Low (0.1+)', value: '0.1' },
];

const DATE_RANGE_OPTIONS = WAZUH_DATE_RANGE_OPTIONS.map((option) => ({
  label: option,
  value: option,
}));

const VulnerabilityDetection: FC = () => {
  const navigate = useNavigate();
  const { dateRange, setDateRange, isReseller } = useWazuhScope();
  const { filters, setFilter, search, searchInput, setSearchInput, resetFilters } =
    useSecurityPageFilters(SECURITY_PAGE_FILTER_KEYS.vulnerabilityDetection, {
      defaults: SECURITY_VULNERABILITY_FILTER_DEFAULTS,
    });
  const {
    severity = '',
    status = '',
    cvss = '',
    osPlatform = '',
    organizationId = '',
    wazuhAgentId = '',
    packageName = '',
  } = filters;
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [sortParams, setSortParams] = useState<VulnerabilitySortParams | null>(null);
  const [resetSortFlag, setResetSortFlag] = useState(false);
  const [packageSearch, setPackageSearch] = useState('');

  const vulnerabilityFilters = useMemo(
    () => ({
      severity: filters.severity,
      status: filters.status,
      minCvss: filters.cvss ? Number(filters.cvss) : undefined,
      osPlatform: filters.osPlatform,
      organizationId: filters.organizationId,
      wazuhAgentId: filters.wazuhAgentId,
      packageName: filters.packageName,
    }),
    [filters]
  );

  const openVulnerabilityDetail = useCallback(
    (item: WazuhVulnerabilityItem) => {
      const id = getVulnerabilityId(item);
      if (!id) return;
      navigate(getSecurityVulnerabilityDetailHref(id), {
        state: { record: item },
      });
    },
    [navigate]
  );

  const { loading, summary, items: paged, facets } = useVulnerabilitiesData(
    page,
    rowsPerPage,
    search,
    vulnerabilityFilters,
    sortParams
  );

  const packageOptions = useMemo(() => {
    const query = packageSearch.trim().toLowerCase();
    return facets.packages
      .filter((name) => !query || name.toLowerCase().includes(query))
      .map((name) => ({ value: name, label: toFilterListLabel(name) }));
  }, [facets.packages, packageSearch]);

  const { exporting, handleExport } = useVulnerabilityExport(
    search,
    vulnerabilityFilters,
    sortParams
  );

  const items = paged?.items ?? [];
  const totalRecords = paged?.totalRecords ?? 0;

  const toolbarFilters: EdrToolbarFilter[] = useMemo(
    () => [
      {
        id: 'cvss',
        label: 'CVSS Score',
        icon: <SpeedIcon style={{ fontSize: 14 }} />,
        options: CVSS_OPTIONS,
        value: cvss,
        onChange: (value) => {
          setFilter('cvss', value);
          setPage(0);
        },
      },
      {
        id: 'severity',
        label: 'Severity',
        icon: <WarningIcon style={{ fontSize: 14 }} />,
        options: SEVERITY_OPTIONS,
        value: severity,
        onChange: (value) => {
          setFilter('severity', value);
          setPage(0);
        },
      },
      {
        id: 'status',
        label: 'Status',
        options: STATUS_OPTIONS,
        value: status,
        onChange: (value) => {
          setFilter('status', value);
          setPage(0);
        },
      },
      {
        id: 'os',
        label: 'Operating System',
        icon: <ComputerIcon style={{ fontSize: 14 }} />,
        options: facets.operatingSystems.map((name) => ({
          label: toFilterListLabel(name),
          value: name,
        })),
        value: osPlatform,
        onChange: (value) => {
          setFilter('osPlatform', value);
          setPage(0);
        },
      },
      {
        id: 'endpoint',
        label: 'Endpoint',
        variant: 'searchableEndpoint' as const,
        pageOrganizationId: organizationId || undefined,
        value: wazuhAgentId,
        onChange: (value: string) => {
          setFilter('wazuhAgentId', value);
          setPage(0);
        },
      },
      ...(isReseller
        ? [
          {
            id: 'org',
            label: 'Organization',
            variant: 'resellerClient' as const,
            value: organizationId,
            onChange: (value: string) => {
              setFilter('organizationId', value);
              setFilter('wazuhAgentId', '');
              setPage(0);
            },
          },
        ]
        : []),

      {
        id: 'package',
        label: 'Packages',
        variant: 'searchable' as const,
        value: packageName,
        searchableOptions: packageOptions,
        searchableOnSearch: setPackageSearch,
        onChange: (value: string) => {
          setFilter('packageName', value);
          setPackageSearch('');
          setPage(0);
        },
      },
      {
        id: 'date',
        label: 'Date Range',
        icon: <CalendarTodayIcon style={{ fontSize: 14 }} />,
        options: DATE_RANGE_OPTIONS,
        value: dateRange,
        onChange: (value) => {
          setDateRange(value as WazuhDateRangeOption);
          setPage(0);
        },
      },
    ],
    [
      severity,
      status,
      cvss,
      osPlatform,
      organizationId,
      wazuhAgentId,
      packageName,
      dateRange,
      isReseller,
      facets.operatingSystems,
      packageOptions,
      setFilter,
      setDateRange,
    ]
  );

  const handleResetFiltersBase = useSecurityEdrToolbarReset(resetFilters, setDateRange, setPage);
  const handleResetFilters = () => {
    handleResetFiltersBase();
    setPackageSearch('');
    setSortParams(null);
    setResetSortFlag((prev) => !prev);
  };

  const handleSort = (columnId: string, direction: 'asc' | 'desc' | null) => {
    setSortParams(direction ? { column: columnId, sort: direction } : null);
    setPage(0);
  };

  const columns: Column[] = useMemo(
    () => [
      {
        id: 'cveId',
        label: 'Event Name',
        sortable: true,
        width: 160,
        render: (row) => {
          const item = row as unknown as WazuhVulnerabilityItem;
          const title = item.title?.trim() || '—';
          const cveId = item.cveId?.trim() || '—';
          return (
            <Tooltip title={title !== '—' ? title : ''} placement="top" enterDelay={300}>
              <Box style={{ maxWidth: 160, minWidth: 0, overflow: 'hidden' }}>
                <Typography noWrap style={{ fontSize: 12, fontWeight: 600, color: '#1E293B' }}>
                  {title}
                </Typography>
                <Typography noWrap style={{ ...edrMutedStyle, fontSize: 11 }}>
                  {cveId}
                </Typography>
              </Box>
            </Tooltip>
          );
        },
      },
      {
        id: 'cvssScore',
        label: 'CVSS',
        sortable: true,
        render: (row) => <CvssCell score={(row as unknown as WazuhVulnerabilityItem).cvssScore} />,
      },
      {
        id: 'severity',
        label: 'Severity',
        sortable: true,
        render: (row) => (
          <EdrSeverityPill
            level={vulnerabilitySeverityToLevel((row as unknown as WazuhVulnerabilityItem).severity)}
            label={toVulnerabilityTitle((row as unknown as WazuhVulnerabilityItem).severity)}
          />
        ),
      },
      {
        id: 'status',
        label: 'Status',
        sortable: true,
        render: (row) => (
          <EdrStatusPill
            tone={vulnerabilityStatusToTone((row as unknown as WazuhVulnerabilityItem).status)}
            label={toVulnerabilityTitle((row as unknown as WazuhVulnerabilityItem).status)}
          />
        ),
      },
      {
        id: 'osPlatform',
        label: 'Operating System',
        sortable: true,
        render: (row) => (
          <span style={edrMutedStyle}>
            {(row as unknown as WazuhVulnerabilityItem).osPlatform}
          </span>
        ),
      },
      {
        id: 'endpoint',
        label: 'Endpoint',
        sortable: true,
        render: (row) => (
          <span style={edrMonoStyle}>{(row as unknown as WazuhVulnerabilityItem).endpoint}</span>
        ),
      },
      {
        id: 'organization',
        label: 'Organization',
        sortable: true,
        render: (row) => (
          <span style={edrMutedStyle}>
            {(row as unknown as WazuhVulnerabilityItem).organization}
          </span>
        ),
      },
      {
        id: 'packageName',
        label: 'Package',
        sortable: true,
        render: (row) => (
          <span style={edrMutedStyle}>
            {(row as unknown as WazuhVulnerabilityItem).packageName}
          </span>
        ),
      },
      {
        id: 'detectedAt',
        label: 'Detected',
        sortable: true,
        render: (row) => (
          <EdrDateTime value={(row as unknown as WazuhVulnerabilityItem).detectedAt} />
        ),
      },
    ],
    []
  );

  return (
    <>
      <Helmet>
        <title>{getPageTitle('Security — Vulnerabilities')}</title>
      </Helmet>

      <Box
        p={3}
        display="flex"
        flexDirection="column"
        style={{ gap: 16, minHeight: '100%', boxSizing: 'border-box' }}
      >
        <EdrPageHeader
          breadcrumb={breadcrumb}
          title="Vulnerabilities"
          subtitle="Identify, prioritize, and remediate security vulnerabilities across protected endpoints."
          actions={(
            <Button
              variant="contained"
              color="primary"
              startIcon={<GetAppIcon />}
              style={{ textTransform: 'none' }}
              loading={exporting}
              onClick={handleExport}
            >
              Export
            </Button>
          )}
        />

        <Box display="grid" style={{ gridTemplateColumns: 'repeat(4, minmax(0, 1fr))', gap: 16 }}>
          <EdrKpiCard
            label="Total Vulnerabilities"
            value={(
              <>
                {formatNumber(summary?.activeVulnerabilities)}
                <EdrStatusPill tone="high" label="Open" dot={false} />
              </>
            )}
            icon={<BugReportIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.high}
            accent={EDR_COLORS.high}
          >
            <EdrStatusPill tone="neutral" label={`${formatNumber(summary?.totalVulnerabilities)} Total`} />
            <EdrStatusPill tone="success" label={`${formatNumber(summary?.resolvedVulnerabilities)} Resolved`} />
          </EdrKpiCard>
          <EdrKpiCard
            label="Critical Vulnerabilities"
            value={(
              <>
                {formatNumber(summary?.activeCriticalVulnerabilities)}
                <EdrStatusPill tone="critical" label="Open" dot={false} />
              </>
            )}
            icon={<WarningIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.critical}
            accent={EDR_COLORS.critical}
          >
            <EdrStatusPill tone="neutral" label={`${formatNumber(summary?.criticalVulnerabilities)} Total`} />
            <EdrStatusPill
              tone="success"
              label={`${formatNumber(summary?.resolvedCriticalVulnerabilities)} Resolved`}
            />
          </EdrKpiCard>
          <EdrKpiCard
            label="High Risk Endpoints"
            value={formatNumber(summary?.highRiskEndpoints)}
            icon={<ComputerIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.high}
            accent={EDR_COLORS.high}
          >
            Endpoints most exposed to risk
          </EdrKpiCard>
          <EdrKpiCard
            label="Remediation"
            value={`${formatNumber(summary?.remediationPercent)}%`}
            icon={<TrendingUpIcon style={{ fontSize: 18 }} />}
            iconColor={EDR_COLORS.success}
            accent={EDR_COLORS.success}
          >
            Progress towards closing vulnerabilities
          </EdrKpiCard>
        </Box>

        <EdrDataCard
          toolbar={(
            <EdrDataToolbar
              searchValue={searchInput}
              onSearchChange={(v) => {
                setSearchInput(v);
                setPage(0);
              }}
              searchPlaceholder="Search Vulnerabilities…"
              filters={toolbarFilters}
              onReset={handleResetFilters}
            />
          )}
        >
          <Table
            columns={columns}
            data={items as unknown as TableData[]}
            loading={loading}
            showPagination
            page={page}
            rowsPerPage={rowsPerPage}
            totalRows={totalRecords}
            onPageChange={setPage}
            onRowsPerPageChange={(next) => {
              setRowsPerPage(next);
              setPage(0);
            }}
            onSort={handleSort}
            resetSortFlag={resetSortFlag}
            onRowClick={(row) => openVulnerabilityDetail(row as unknown as WazuhVulnerabilityItem)}
            TableContainerProps={{ sx: { boxShadow: 'none', borderRadius: 0 } }}
          />
        </EdrDataCard>
      </Box>
    </>
  );
};

export default VulnerabilityDetection;
